import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Custom hook to check API health with exponential backoff
 * @param {Object} options - Configuration options
 * @param {number} options.initialDelay - Initial delay in ms (default: 2000)
 * @param {number} options.maxDelay - Maximum delay in ms (default: 8000)
 * @param {number} options.maxRetries - Maximum number of retries (default: 10)
 * @returns {Object} - { isHealthy: boolean, isLoading: boolean, error: string | null }
 */
export const useHealthCheck = (options = {}) => {
  const {
    initialDelay = 2000,
    maxDelay = 8000,
    maxRetries = 10
  } = options;

  const [isHealthy, setIsHealthy] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let retryCount = 0;
    let timeoutId = null;
    let isCancelled = false;

    const checkHealth = async (delay = initialDelay) => {
      if (isCancelled) return;

      try {
        setIsLoading(true);
        setError(null);

        // Use HEAD request for health check (lighter than GET)
        const response = await axios.head(`${BACKEND_URL}/health`, {
          timeout: 5000,
          validateStatus: (status) => status === 200 || status === 503 // Accept 200 (healthy) or 503 (unhealthy but server responding)
        });

        // 200 means healthy
        if (response.status === 200) {
          setIsHealthy(true);
          setIsLoading(false);
          return;
        }
        
        // 503 means unhealthy but server is responding - continue retrying
        // This will be caught below and trigger retry logic
        throw new Error('API is starting up...');
      } catch (err) {
        if (isCancelled) return;

        // If it's a network error or 502/503, server might be sleeping
        const isServerDown = !err.response || 
                            err.response.status >= 500 ||
                            err.code === 'ECONNREFUSED' ||
                            err.code === 'ETIMEDOUT';

        if (isServerDown && retryCount < maxRetries) {
          retryCount++;
          const nextDelay = Math.min(delay * 2, maxDelay);
          
          setError(`API is starting up... (attempt ${retryCount}/${maxRetries})`);
          
          timeoutId = setTimeout(() => {
            checkHealth(nextDelay);
          }, delay);
        } else {
          // Max retries reached or non-recoverable error
          setIsHealthy(false);
          setIsLoading(false);
          setError(err.response?.status === 503 
            ? 'API is temporarily unavailable' 
            : 'Unable to connect to API');
        }
      }
    };

    // Start health check
    checkHealth();

    // Cleanup
    return () => {
      isCancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [initialDelay, maxDelay, maxRetries]);

  return { isHealthy, isLoading, error };
};

